import api from '../utilities/api';
import { API_URL } from '../config';
import { getAccessToken } from '../utilities/storage/tokenStorage';
import { StatsWeekAnchor } from '../models/statistics';
import type {
    StatsRequest,
    StatsResponse,
    ConnectionStats,
    MediaImportStats,
    GameCreationStats,
    MediaConsultationStats,
    GamePlayStats,
    PerformanceStats,
    GameDurationStats,
    GameSatisfactionStats,
    ScoringStats
} from '../models/statistics';

const statsUrl = API_URL + '/stats';

const getFreshToken = async (): Promise<string> => (await getAccessToken()) || '';

// Every aggregated-stats request pins week buckets on the period start date so the
// API bucketing matches the labels generated client-side (see generateLabels)
const withPeriodStartWeekAnchor = (request: StatsRequest): StatsRequest => ({
    ...request,
    weekAnchor: StatsWeekAnchor.PERIOD_START
});

// ============================================================================
// Session Management
// ============================================================================

export const startSession = async (): Promise<any> => {
    try {
        const response = await api.post('/stats/session/start', {});
        return response.data;
    } catch (error) {
        console.error('Error starting session:', error);
        return null;
    }
};

export const getScoringStats = async (request: StatsRequest): Promise<ScoringStats | null> => {
    try {
        const response = await api.post<{
            data: { labels: string[]; scores: number[]; gameCounts: number[] };
            evolution?: number;
        }>('/stats/game/scoring', {
            ...withPeriodStartWeekAnchor(request),
            type: request.type || 'ALL'
        });

        return {
            labels: response.data.data.labels || [],
            scores: response.data.data.scores || [],
            gameCounts: response.data.data.gameCounts || [],
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching scoring stats:', error);
        return null;
    }
};

export const getGameSatisfactionStats = async (
    request: StatsRequest
): Promise<GameSatisfactionStats | null> => {
    try {
        const response = await api.post<{
            data: {
                labels: string[];
                satisfactionRate: number[];
                totalVotes: number[];
                noOpinionVotes: number[];
                totalVotesAll?: number;
                noOpinionVotesAll?: number;
                globalAverageRating?: number;
                globalSatisfactionPercent?: number;
            };
        }>('/stats/game/satisfaction-by-type', { ...request });

        return {
            labels: response.data.data.labels || [],
            satisfactionRate: response.data.data.satisfactionRate || [],
            totalVotes: response.data.data.totalVotes || [],
            noOpinionVotes: response.data.data.noOpinionVotes || [],
            totalVotesAll: response.data.data.totalVotesAll,
            noOpinionVotesAll: response.data.data.noOpinionVotesAll,
            globalAverageRating: response.data.data.globalAverageRating,
            globalSatisfactionPercent: response.data.data.globalSatisfactionPercent
        };
    } catch (error) {
        console.error('Error fetching game satisfaction stats:', error);
        return null;
    }
};

export const endSession = async (
    sessionId: string,
    duration: number,
    useKeepalive = false
): Promise<any> => {
    try {
        if (useKeepalive) {
            const token = await getFreshToken();
            const response = await fetch(`${statsUrl}/session/end`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId, duration }),
                keepalive: true
            });

            if (!response.ok) {
                console.error('Failed to end session:', response.status);
                return null;
            }
            return await response.json();
        }

        const response = await api.post('/stats/session/end', { sessionId, duration });
        return response.data;
    } catch (error) {
        console.error('Error ending session:', error);
        return null;
    }
};

export const startView = async (payload: { action?: string; details: any }): Promise<any> => {
    try {
        const response = await api.post('/stats/activity/start', payload);
        return response.data;
    } catch (error) {
        console.error('Error starting view activity:', error);
        return null;
    }
};

export const endView = async (
    payload: { action?: string; details: any },
    useKeepalive = false
): Promise<any> => {
    try {
        if (useKeepalive) {
            const token = await getFreshToken();
            const endpoint = `${statsUrl}/activity/end`;
            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload),
                    keepalive: true
                });
                if (response.ok) return await response.json();
                console.warn(
                    'Keepalive fetch returned non-ok, will try sendBeacon fallback',
                    response.status
                );
            } catch (e) {
                console.warn('Keepalive fetch failed, will try sendBeacon fallback', e);
            }

            if (typeof navigator !== 'undefined' && (navigator as any).sendBeacon) {
                try {
                    const beaconPayload = { ...payload, authToken: token };
                    const blob = new Blob([JSON.stringify(beaconPayload)], {
                        type: 'application/json'
                    });
                    const ok = (navigator as any).sendBeacon(endpoint, blob);
                    return { ok: !!ok, beacon: true };
                } catch (e) {
                    console.warn('sendBeacon failed', e);
                }
            }

            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                if (!response.ok) {
                    console.error('Failed to end view activity (no-keepalive):', response.status);
                    return null;
                }
                return await response.json();
            } catch (e) {
                console.error('Failed to end view activity (final fallback):', e);
                return null;
            }
        }

        const response = await api.post('/stats/activity/end', payload);
        return response.data;
    } catch (error) {
        console.error('Error ending view activity:', error);
        return null;
    }
};

// ============================================================================
// Game Statistics
// ============================================================================

export const startGame = async (gameId: string, gameType?: string): Promise<any> => {
    try {
        const payload: any = { game_id: gameId, timestamp: new Date().toISOString() };
        if (gameType) payload.game_type = gameType;

        const response = await api.post(`/stats/game-start/${gameId}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error recording game start:', error);
        return null;
    }
};

export const insertStats = async (data: any): Promise<any> => {
    try {
        const gameId = data.idGame;
        const payload = {
            duration: data.timeTakenToCompleteGame,
            level: data.difficultyLevel,
            errorCount: data.errorCount,
            clueCount: data.clueCount
        };
        const response = await api.post(`/stats/game-completion/${gameId}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error inserting game stats:', error);
        return null;
    }
};

export const insertSatisfaction = async (
    gameId: string,
    rating: number,
    gameType?: string
): Promise<any> => {
    try {
        const payload: any = { rating };
        if (gameType) payload.game_type = gameType;

        const response = await api.post(`/stats/game-satisfaction/${gameId}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error inserting game satisfaction:', error);
        return null;
    }
};

export const getGamePlayStats = async (request: StatsRequest): Promise<GamePlayStats | null> => {
    try {
        const response = await api.post<{
            data: {
                start: number[];
                finish: number[];
                totalStarted?: number;
                totalFinished?: number;
            };
            evolution?: number;
        }>('/stats/game/play', {
            ...withPeriodStartWeekAnchor(request),
            type: request.type || 'ALL'
        });
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);
        return {
            labels,
            started: response.data.data.start || [],
            finished: response.data.data.finish || [],
            totalStarted: response.data.data.totalStarted,
            totalFinished: response.data.data.totalFinished,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching game play stats:', error);
        return null;
    }
};

export const getPerformanceStats = async (
    request: StatsRequest
): Promise<PerformanceStats | null> => {
    try {
        const body = { ...withPeriodStartWeekAnchor(request), type: request.type || 'ALL' };
        const [levelResponse, errorResponse, clueResponse] = await Promise.all([
            api.post<{
                data: {
                    totalLevel: number;
                    averageLevel?: number;
                    medianLevel?: number;
                    level: number[];
                };
                evolution?: number;
            }>('/stats/game/level', body),
            api.post<{ data: { totalError: number; error: number[] } }>('/stats/game/error', body),
            api.post<{ data: { totalClue: number; clue: number[] } }>('/stats/game/clue', body)
        ]);
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);
        return {
            labels,
            difficulty: levelResponse.data.data.level || [],
            averageLevel: levelResponse.data.data.averageLevel,
            medianDifficulty: levelResponse.data.data.medianLevel,
            errors: errorResponse.data.data.error || [],
            clues: clueResponse.data.data.clue || [],
            evolution: levelResponse.data.evolution
        };
    } catch (error) {
        console.error('Error fetching performance stats:', error);
        return null;
    }
};

export const getGameDurationStats = async (
    request: StatsRequest
): Promise<GameDurationStats | null> => {
    try {
        const body = { ...withPeriodStartWeekAnchor(request), type: request.type || 'ALL' };
        const [levelResponse, durationResponse] = await Promise.all([
            api.post<{ data: { totalLevel: number; medianLevel?: number; level: number[] } }>(
                '/stats/game/level',
                body
            ),
            api.post<{
                data: {
                    totalDuration: number;
                    totalMinutes?: number;
                    median?: number;
                    medianSeconds?: number;
                    duration: number[];
                    durationSeconds?: number[];
                };
                evolution?: number;
            }>('/stats/game/duration', body)
        ]);
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);
        return {
            labels,
            averageLevel: levelResponse.data.data.level || [],
            medianLevel: levelResponse.data.data.medianLevel,
            medianDuration: durationResponse.data.data.duration || [],
            medianDurationSeconds: durationResponse.data.data.durationSeconds,
            median: durationResponse.data.data.median,
            medianSeconds: durationResponse.data.data.medianSeconds,
            totalDuration: durationResponse.data.data.totalDuration,
            totalMinutes: durationResponse.data.data.totalMinutes,
            evolution: durationResponse.data.evolution
        };
    } catch (error) {
        console.error('Error fetching game duration stats:', error);
        return null;
    }
};

// ============================================================================
// Media Statistics
// ============================================================================

export const lookupMedia = async (mediaId: string, duration: number): Promise<any> => {
    try {
        const response = await api.post(`/stats/media-watch/${mediaId}`, { duration });
        return response.data;
    } catch (error) {
        console.error('Error recording media lookup:', error);
        return null;
    }
};

// ============================================================================
// Connection Statistics
// ============================================================================

export const getActivityTimeStats = async (
    request: StatsRequest
): Promise<ConnectionStats | null> => {
    try {
        const response = await api.post<{
            data: number[];
            median?: number;
            totalMinutes?: number;
            evolution?: number;
        }>('/stats/activity-time', withPeriodStartWeekAnchor(request));
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);
        return {
            labels,
            data: response.data.data || [],
            median: response.data.median,
            totalMinutes: response.data.totalMinutes,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching activity time stats:', error);
        return null;
    }
};

const getActionStats = async (
    request: StatsRequest,
    action: string
): Promise<{ labels: string[]; data: number[]; total?: number; evolution?: number } | null> => {
    try {
        const response = await api.post<{ data: number[]; total?: number; evolution?: number }>(
            '/stats/activity/action',
            { ...withPeriodStartWeekAnchor(request), action }
        );
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);
        return {
            labels,
            data: response.data.data || [],
            total: response.data.total,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error(`Error fetching action stats for ${action}:`, error);
        return null;
    }
};

export const getGameCreationStats = (request: StatsRequest): Promise<GameCreationStats | null> => {
    return getActionStats(request, 'created_game');
};

export const getMediaCreationStats = (request: StatsRequest): Promise<MediaImportStats | null> => {
    return getActionStats(request, 'created_media');
};

export const getMediaConsultationStats = async (
    request: StatsRequest
): Promise<MediaConsultationStats | null> => {
    try {
        const response = await api.post<{
            data: { totalDuration: number; duration: number[] };
            evolution?: number;
        }>('/stats/media/consultation', withPeriodStartWeekAnchor(request));
        const labels = generateLabels(request.startDate, request.endDate, request.timeScale);
        return {
            labels,
            duration: response.data.data.duration || [],
            totalDuration: response.data.data.totalDuration,
            evolution: response.data.evolution
        };
    } catch (error) {
        console.error('Error fetching media consultation stats:', error);
        return null;
    }
};

// ============================================================================
// Utility Functions
// ============================================================================

const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;

// 7-day week buckets anchored on the period start date — MUST mirror the API bucketing
// (StimulinAPI services/stats/StatsUtilsService.ts) used when weekAnchor = periodStart
const getAnchoredWeekStart = (date: Date, periodStart: Date): Date => {
    const dayUtc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    const anchorUtc = Date.UTC(
        periodStart.getUTCFullYear(),
        periodStart.getUTCMonth(),
        periodStart.getUTCDate()
    );
    if (dayUtc < anchorUtc) return new Date(anchorUtc);
    const daysSinceStart = Math.floor((dayUtc - anchorUtc) / MS_PER_DAY);
    const weekIndex = Math.floor(daysSinceStart / DAYS_PER_WEEK);
    return new Date(anchorUtc + weekIndex * DAYS_PER_WEEK * MS_PER_DAY);
};

const getPeriodKey = (date: Date, period: string, periodStart: Date): string => {
    const normalized = new Date(date);

    switch (period) {
        case 'day':
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'DD/MM', true);
        case 'week':
            return formatDate(getAnchoredWeekStart(normalized, periodStart), 'DD/MM', true);
        case 'month':
            normalized.setUTCDate(1);
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'MM/YYYY', true);
        case 'year':
            normalized.setUTCMonth(0, 1);
            normalized.setUTCHours(0, 0, 0, 0);
            return formatDate(normalized, 'YYYY', true);
        default:
            return date.toISOString();
    }
};

const generateLabels = (startDate: string, endDate: string, period: string): string[] => {
    const labels: string[] = [];
    const labelsSet = new Set<string>();
    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start);

    current.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(23, 59, 59, 999);

    while (current <= end) {
        const key = getPeriodKey(current, period, start);
        if (!labelsSet.has(key)) {
            labelsSet.add(key);
            labels.push(key);
        }
        current.setUTCDate(current.getUTCDate() + 1);
    }

    return labels;
};

const formatDate = (date: Date, format: string, useUTC = false): string => {
    const day = String(useUTC ? date.getUTCDate() : date.getDate()).padStart(2, '0');
    const month = String((useUTC ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0');
    const year = useUTC ? date.getUTCFullYear() : date.getFullYear();

    return format.replace('DD', day).replace('MM', month).replace('YYYY', String(year));
};

// Default period shown on the statistics view: last 3 months, same day of month
// (e.g. 17/04 -> 17/07) — same formula as the '3 derniers mois' preset
export const getDefaultStatsPeriod = (): { startDate: Date; endDate: Date } => {
    const now = new Date();

    return {
        startDate: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
        endDate: now
    };
};

export const getDatePresets = () => {
    const now = new Date();

    return [
        {
            label: '7 derniers jours',
            startDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
            endDate: now
        },
        {
            label: '30 derniers jours',
            startDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
            endDate: now
        },
        {
            label: '3 derniers mois',
            ...getDefaultStatsPeriod()
        },
        {
            label: 'Cette année',
            startDate: new Date(now.getFullYear(), 0, 1),
            endDate: now
        },
        {
            label: 'Année dernière',
            startDate: new Date(now.getFullYear() - 1, 0, 1),
            endDate: new Date(now.getFullYear() - 1, 11, 31)
        }
    ];
};
